import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';

const features = [
  {
    icon: (
      <svg width='28' height='28' fill='none'>
        <circle cx='14' cy='14' r='12' fill='#6C4DF6' />
        <path
          d='M10 14l3 3 5-5'
          stroke='#fff'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    title: 'Instant Response',
    desc: '24/7 automated acknowledgment with human follow-up'
  },
  {
    icon: (
      <svg width='28' height='28' fill='none'>
        <circle cx='14' cy='14' r='12' fill='#1A1A2E' />
        <path
          d='M14 9v5l3 2'
          stroke='#fff'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    title: 'Military-Grade Security',
    desc: 'End-to-end encrypted communications'
  },
  {
    icon: (
      <svg width='28' height='28' fill='none'>
        <circle cx='14' cy='14' r='12' fill='#FF7BA9' />
        <path
          d='M14 10v4l2 2'
          stroke='#fff'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    title: 'AI-Powered Matching',
    desc: 'Smart routing to the perfect team member'
  }
];

const Form = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Particle system refs
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const formRef = useRef();

  // Update EmailJS initialization with the correct public key
  useEffect(() => {
    emailjs.init('GYwxqXX-VPrWQJk_k');
  }, []);

  // Particle animation setup/cleanup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let particles = [];
    const w = (canvas.width = window.innerWidth);
    const h = (canvas.height = window.innerHeight);
    for (let i = 0; i < 40; i++) {
      particles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 2 + 1,
        dx: (Math.random() - 0.5) * 0.7,
        dy: (Math.random() - 0.5) * 0.7,
        color: `rgba(${
          180 + Math.random() * 50
        },${77 + Math.random() * 100},${246 + Math.random() * 9},0.7)`
      });
    }
    function draw() {
      ctx.clearRect(0, 0, w, h);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, 2 * Math.PI);
        ctx.fillStyle = p.color;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > w) p.dx *= -1;
        if (p.y < 0 || p.y > h) p.dy *= -1;
      });
      animationRef.current = requestAnimationFrame(draw);
    }
    draw();
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');
    
    // Apply formatting for US numbers (as placeholder suggests)
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      return !match[2] ? match[1] : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;
    }
    return cleaned;
  };

  const validateIndianPhone = (phone) => {
    // Remove all formatting to check digits only
    const cleaned = phone.replace(/\D/g, '');
    
    // Indian mobile numbers start with 6-9 and are 10 digits
    const indianRegex = /^[6-9]\d{9}$/;
    return indianRegex.test(cleaned);
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setForm({ ...form, phone: formatted });
    
    // Validate only when user has entered something
    if (formatted.replace(/\D/g, '').length > 0) {
      const isValid = validateIndianPhone(formatted);
      setPhoneError(isValid ? '' : 'Please enter a valid Indian mobile number starting with 6-9');
    } else {
      setPhoneError('');
    }
    
    if (error) setError('');
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate all fields including phone
    if (!form.firstName || !form.lastName || !form.email || !form.phone || !form.message) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    // Additional phone validation
    if (!validateIndianPhone(form.phone)) {
      setPhoneError('Please enter a valid Indian mobile number starting with 6-9');
      setIsLoading(false);
      return;
    }

    try {
      const response = await emailjs.sendForm(
        'service_r4rdqel',
        'template_pjn2eva',
        formRef.current,
        'r4h-QQHVf4PaVzNiV'
      );

      if (response.status === 200) {
        setSubmitted(true);
        setForm({ firstName: '', lastName: '', email: '', phone: '', message: '' });
        alert('Message sent successfully!');
        setTimeout(() => setSubmitted(false), 3000);
      }
    } catch (err) {
      console.error('EmailJS Error:', err);
      setError('Message sending failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='main-bg'>
      {/* Particle Canvas */}
      <canvas
        ref={canvasRef}
        className='particle-canvas'
      />
      {/* Floating Shapes */}
      <div className='floating-shape shape1' />
      <div className='floating-shape shape2' />
      <div className='floating-shape shape3' />
      <style>{`
        .main-bg {
          min-height: 100vh;
          width: 100vw;
          background: linear-gradient(135deg, #1A1A2E 60%, #6C4DF6 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }
        .particle-canvas {
          position: absolute;
          top: 0; left: 0;
          width: 100vw;
          height: 100vh;
          pointer-events: none;
          z-index: 0;
        }
        .floating-shape {
          position: absolute;
          border-radius: 50%;
          opacity: 0.25;
          z-index: 2;
          animation: floatShape 8s infinite alternate ease-in-out;
        }
        .shape1 {
          width: 120px; height: 120px;
          background: linear-gradient(135deg, #6C4DF6, #FF7BA9);
          top: 10%; left: 8%;
          animation-delay: 0s;
        }
        .shape2 {
          width: 80px; height: 80px;
          background: linear-gradient(135deg, #1A1A2E, #6C4DF6);
          bottom: 15%; right: 12%;
          animation-delay: 2s;
        }
        .shape3 {
          width: 60px; height: 60px;
          background: linear-gradient(135deg, #FF7BA9, #6C4DF6);
          top: 70%; left: 60%;
          animation-delay: 4s;
        }
        @keyframes floatShape {
          0% { transform: translateY(0) scale(1); }
          100% { transform: translateY(-30px) scale(1.1); }
        }
        .container-flex {
          display: flex;
          width: 100%;
          max-width: 1200px;
          min-height: 600px;
          background: none;
          z-index: 10;
        }
        @media (max-width: 900px) {
          .container-flex {
            flex-direction: column;
            align-items: center;
            min-height: 0;
          }
        }
        .info-panel {
          flex: 1.2;
          background: none;
          color: #fff;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 3rem 2.5rem 3rem 3rem;
          z-index: 3;
          animation: slideLeftIn 1s cubic-bezier(.77,0,.18,1) forwards;
        }
        .info-title {
          font-size: 2.8rem;
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 1.2rem;
          background: linear-gradient(90deg, #6C4DF6 60%, #FF7BA9 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .info-desc {
          font-size: 1.15rem;
          color: #e0e7ff;
          margin-bottom: 2.2rem;
          font-weight: 400;
        }
        .feature-list {
          margin-top: 1.5rem;
        }
        .feature-item {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1.3rem;
        }
        .feature-icon {
          flex-shrink: 0;
        }
        .feature-texts {
          display: flex;
          flex-direction: column;
        }
        .feature-title {
          font-weight: 700;
          font-size: 1.08rem;
          color: #fff;
        }
        .feature-desc {
          font-size: 0.98rem;
          color: #cbd5e1;
        }
        .form-panel {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          z-index: 4;
          animation: slideRightIn 1s cubic-bezier(.77,0,.18,1) forwards;
        }
        @keyframes slideLeftIn {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideRightIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @media (max-width: 900px) {
          .info-panel, .form-panel {
            animation: none;
            padding: 2rem 1rem;
          }
        }
        /* Holographic Card Effect */
        .form-container {
          background: linear-gradient(135deg, #fff 80%, #e0e7ff 100%);
          border-radius: 20px;
          padding: 3rem;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25), 0 0 40px 0 #6C4DF6AA;
          position: relative;
          z-index: 10;
          overflow: hidden;
          background-clip: padding-box;
        }
        .form-container::before {
          content: '';
          position: absolute;
          top: -40px; left: -40px;
          width: 120%;
          height: 120%;
          background: linear-gradient(120deg, rgba(108,77,246,0.12) 0%, rgba(255,123,169,0.09) 100%);
          pointer-events: none;
          z-index: 1;
          filter: blur(12px);
        }
        .form-title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 2rem;
          color: #1A1A2E;
          position: relative;
        }
        .form-title::after {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 0;
          width: 50px;
          height: 4px;
          background: linear-gradient(to right, #6C4DF6, #FF7BA9);
          border-radius: 2px;
        }
        .form-group {
          margin-bottom: 1.5rem;
        }
        .form-group.double {
          display: flex;
          gap: 1rem;
        }
        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
          font-weight: 600;
          color: #1A1A2E;
          opacity: 0.8;
        }
        .form-input {
          width: 100%;
          padding: 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 1rem;
          background-color: #f8fafc;
          transition: border-color 0.3s;
        }
        .form-input:focus {
          outline: none;
          border-color: #6C4DF6;
          background-color: #fff;
        }
        textarea.form-input {
          min-height: 120px;
          resize: vertical;
        }
        .submit-btn {
          width: 100%;
          padding: 1rem;
          border: none;
          border-radius: 10px;
          background: linear-gradient(to right, #6C4DF6, #FF7BA9);
          color: white;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          margin-top: 1rem;
          transition: box-shadow 0.3s, transform 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .submit-btn:hover:not(:disabled) {
          box-shadow: 0 8px 15px -8px #6C4DF680;
          transform: translateY(-2px);
        }
        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .form-footer {
          text-align: center;
          margin-top: 2rem;
          font-size: 0.9rem;
          color: #64748b;
        }
        .error-message {
          color: #ef4444;
          font-size: 0.9rem;
          margin-top: 0.5rem;
          text-align: center;
        }
        .phone-error {
          color: #ef4444;
          font-size: 0.8rem;
          margin-top: 0.3rem;
        }
        /* Loading spinner */
        .spinner {
          width: 18px;
          height: 18px;
          border: 3px solid rgba(255,255,255,0.3);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 1s ease-in-out infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        /* Sequential Animation */
        .form-title, .form-group, .submit-btn, .form-footer {
          opacity: 0;
          transform: translateY(30px);
          animation: fadeInUp 0.7s forwards;
        }
        .form-title { animation-delay: 0.2s; }
        .form-group.double { animation-delay: 0.4s; }
        .form-group:not(.double) { animation-delay: 0.6s; }
        .submit-btn { animation-delay: 0.8s; }
        .form-footer { animation-delay: 1s; }
        @keyframes fadeInUp {
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 600px) {
          .form-container {
            padding: 1.5rem;
          }
          .form-title {
            font-size: 1.3rem;
          }
          .info-title {
            font-size: 2rem;
          }
          .form-group.double {
            flex-direction: column;
            gap: 1.5rem;
          }
        }
      `}</style>
      <div className='container-flex'>
        <div className='info-panel'>
          <div>
            <div className='info-title'>
              Ready to Transform
              <br />
              Your Digital Experience?
            </div>
            <div className='info-desc'>
              Connect with our team of experts who will help you navigate the
              digital landscape with cutting-edge solutions.
            </div>
            <div className='feature-list'>
              {features.map((f, i) => (
                <div className='feature-item' key={i}>
                  <span className='feature-icon'>{f.icon}</span>
                  <span className='feature-texts'>
                    <span className='feature-title'>{f.title}</span>
                    <span className='feature-desc'>{f.desc}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className='form-panel'>
          <div className='form-container'>
            <h2 className='form-title'>Get in touch</h2>
            <form onSubmit={handleSubmit} ref={formRef}>
              <div className='form-group double'>
                <div>
                  <label className='form-label' htmlFor='firstName'>
                    First Name
                  </label>
                  <input
                    className='form-input'
                    type='text'
                    id='firstName'
                    name='firstName'
                    required
                    value={form.firstName}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className='form-label' htmlFor='lastName'>
                    Last Name
                  </label>
                  <input
                    className='form-input'
                    type='text'
                    id='lastName'
                    name='lastName'
                    required
                    value={form.lastName}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className='form-group'>
                <label className='form-label' htmlFor='email'>
                  Email Address
                </label>
                <input
                  className='form-input'
                  type='email'
                  id='email'
                  name='email'
                  required
                  value={form.email}
                  onChange={handleChange}
                />
              </div>
              <div className='form-group'>
                <label className='form-label' htmlFor='phone'>
                  Phone Number (Indian)
                </label>
                <input
                  className='form-input'
                  type='tel'
                  id='phone'
                  name='phone'
                  required
                  value={form.phone}
                  onChange={handlePhoneChange}
                  placeholder='+91 0000000000'
                  maxLength="14" // For formatted US number (xxx) xxx-xxxx
                />
                {phoneError && <div className='phone-error'>{phoneError}</div>}
              </div>
              <div className='form-group'>
                <label className='form-label' htmlFor='message'>
                  Message
                </label>
                <textarea
                  className='form-input'
                  id='message'
                  name='message'
                  required
                  value={form.message}
                  onChange={handleChange}
                />
              </div>
              <button type='submit' className='submit-btn' disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className='spinner'></span>
                    Sending...
                  </>
                ) : submitted ? (
                  'Submitted ✔'
                ) : (
                  'Submit →'
                )}
              </button>
              {error && <div className='error-message'>{error}</div>}
            </form>
            <p className='form-footer'>We'll respond within 24 hours</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Form;